import { useEffect, useState, useCallback } from 'react';
import { useIdentify, useTranslation } from '@shared/core';
import { EditPages, Gift, Invitation, RsvpAdmin } from '@withjoy/joykit/icons';
import { Product, ProductOption, ProductSelectionDialogProps, SelectedValue } from './ProductSelectionDialog.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useToast } from '@withjoy/joykit';
import { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import { useDashboardTelemetry } from '../../../Dashboard.telemetry';
import { difference, isEqual } from 'lodash-es';
import { BasicEventInfoDocument, BasicEventInfoQuery, useSaveEventProductVerticalsMutation } from '@graphql/generated';

const getOptions = (productVerticals: ProductVerticals): string[] => {
  const options: string[] = [];

  Object.keys(productVerticals).forEach(key => {
    if (!productVerticals[key as keyof ProductVerticals]) {
      options.push(key.replace('optOutOf', ''));
    }
  });

  return options;
};

export const useProductSelectionDialogController = ({ isOpen, onClose, refreshDashboardPreview }: ProductSelectionDialogProps) => {
  const { eventInfo, eventHandle: eventInfoEventHandle } = useEventInfo();
  const productVerticals = eventInfo?.productVerticals;
  const eventId = eventInfo?.eventId || '';
  const eventHandle = eventInfoEventHandle || '';
  const { identify } = useIdentify();
  const { productSelectionSave } = useDashboardTelemetry();
  const [saveEventProductVerticals, { loading: loadingSaveEventProductVerticals }] = useSaveEventProductVerticalsMutation();
  const { t2 } = useTranslation('productSelectionDialog');
  const { title, subtitle, website, registry, invitations, guestList, cancel: cancelLabel, save: saveLabel, success, error } = t2('text');
  const { toast } = useToast();
  const [selectedIds, setSelectedIds] = useState<SelectedValue>({});

  const toggleProduct = (id: Product) => {
    setSelectedIds(prevSelected => ({
      ...prevSelected,
      [id]: !prevSelected?.[id]
    }));
  };

  const products: ProductOption[] = [
    { id: Product.Website, label: website, icon: EditPages },
    { id: Product.Registry, label: registry, icon: Gift },
    { id: Product.Invitations, label: invitations, icon: Invitation },
    { id: Product.GuestList, label: guestList, icon: RsvpAdmin }
  ];

  useEffect(() => {
    if (isOpen && productVerticals) {
      setSelectedIds({
        [Product.Website]: !productVerticals?.optOutOfWebsite,
        [Product.Registry]: !productVerticals?.optOutOfRegistry,
        [Product.Invitations]: !productVerticals?.optOutOfInvites,
        [Product.GuestList]: !productVerticals?.optOutOfGuestList
      });
    }
  }, [productVerticals, isOpen]);

  const onProductSelectionSave = useCallback(
    async (selectedProductVerticals: ProductVerticals): Promise<void> => {
      const selectedOptions = getOptions(selectedProductVerticals);
      const initialOptions = getOptions(productVerticals as ProductVerticals);
      const removedOptions = difference(initialOptions, selectedOptions);
      const addedOptions = difference(selectedOptions, initialOptions);

      identify(
        { latestInterests: selectedOptions },
        {
          customMerge(key) {
            if (key === 'latestInterests') {
              return (source, destination) => destination;
            }
            return;
          }
        }
      );

      if (!eventId || isEqual(selectedOptions, initialOptions)) {
        onClose();
        return;
      }

      productSelectionSave(selectedOptions, removedOptions, addedOptions);

      saveEventProductVerticals({
        variables: {
          eventId,
          payload: selectedProductVerticals
        },
        update: (cache, { data }) => {
          const queryBaseProps = {
            query: BasicEventInfoDocument,
            variables: { eventHandle }
          };

          const cacheItem = cache.readQuery<BasicEventInfoQuery>({
            ...queryBaseProps
          });

          if (cacheItem?.eventByName?.productVerticals) {
            cache.writeQuery({
              ...queryBaseProps,
              data: {
                ...cacheItem,
                eventByName: {
                  ...cacheItem.eventByName,
                  productVerticals: {
                    ...data?.saveEventProductVerticals
                  }
                }
              }
            });
          }
        }
      }).then(info => {
        if (info.errors) {
          toast(error);
          return;
        }
        refreshDashboardPreview?.();
        onClose();
        toast(success);
      });
    },
    [productVerticals, identify, eventId, productSelectionSave, saveEventProductVerticals, onClose, eventHandle, refreshDashboardPreview, toast, success, error]
  );

  const handleSave = useCallback(async () => {
    await onProductSelectionSave({
      optOutOfGuestList: !selectedIds[Product.GuestList],
      optOutOfInvites: !selectedIds[Product.Invitations],
      optOutOfRegistry: !selectedIds[Product.Registry],
      optOutOfWebsite: !selectedIds[Product.Website]
    });
  }, [selectedIds, onProductSelectionSave]);

  return {
    title,
    subtitle,
    cancelLabel,
    saveLabel,
    products,
    toggleProduct,
    selectedProducts: selectedIds,
    handleSave,
    loadingSaveEventProductVerticals
  };
};
